<script setup>
import { ref } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: { type: Object, required: true },
  agents: { type: Array, default: () => [] },
  teams: { type: Array, default: () => [] },
  inboxes: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);
const { t } = useI18n();
const showDetails = ref(false);

const update = (key, value) => {
  emit('update:modelValue', { ...props.modelValue, [key]: value });
};

const reset = () => {
  emit('update:modelValue', {
    reminderState: 'all',
    rescueLevel: '',
    agentId: '',
    teamId: '',
    inboxId: '',
    fromDate: '',
    toDate: '',
  });
};

const stateOptions = [
  { value: 'all', label: t('INBOX.REMINDER_FILTER.STATE.ALL') },
  { value: 'active', label: t('INBOX.REMINDER_FILTER.STATE.ACTIVE') },
  { value: 'recovered', label: t('INBOX.REMINDER_FILTER.STATE.RECOVERED') },
];
</script>

<template>
  <div class="border-y border-n-weak px-3 py-2">
    <div class="flex items-center gap-1">
      <button
        v-for="option in stateOptions"
        :key="option.value"
        type="button"
        class="rounded-md px-2 py-1 text-xs font-medium"
        :class="
          modelValue.reminderState === option.value
            ? 'bg-n-brand text-white'
            : 'bg-n-alpha-2 text-n-slate-11 hover:text-n-slate-12'
        "
        @click="update('reminderState', option.value)"
      >
        {{ option.label }}
      </button>
      <button
        type="button"
        class="ml-auto text-xs text-n-blue-11"
        @click="showDetails = !showDetails"
      >
        {{
          showDetails
            ? t('INBOX.REMINDER_FILTER.COLLAPSE')
            : t('INBOX.REMINDER_FILTER.MORE')
        }}
      </button>
    </div>

    <div v-if="showDetails" class="mt-2 grid grid-cols-2 gap-2">
      <select
        class="h-8 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-xs text-n-slate-12"
        :value="modelValue.rescueLevel"
        @change="update('rescueLevel', $event.target.value)"
      >
        <option value="">{{ t('INBOX.REMINDER_FILTER.ALL_LEVELS') }}</option>
        <option value="0">
          {{ t('INBOX.REMINDER_FILTER.LEVEL.REMINDER') }}
        </option>
        <option value="1">
          {{ t('INBOX.REMINDER_FILTER.LEVEL.TIMEOUT') }}
        </option>
        <option value="2">
          {{ t('INBOX.REMINDER_FILTER.LEVEL.ESCALATED') }}
        </option>
      </select>
      <select
        class="h-8 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-xs text-n-slate-12"
        :value="modelValue.agentId"
        @change="update('agentId', $event.target.value)"
      >
        <option value="">{{ t('INBOX.REMINDER_FILTER.ALL_AGENTS') }}</option>
        <option v-for="agent in agents" :key="agent.id" :value="agent.id">
          {{ agent.name }}
        </option>
      </select>
      <select
        class="h-8 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-xs text-n-slate-12"
        :value="modelValue.teamId"
        @change="update('teamId', $event.target.value)"
      >
        <option value="">{{ t('INBOX.REMINDER_FILTER.ALL_TEAMS') }}</option>
        <option v-for="team in teams" :key="team.id" :value="team.id">
          {{ team.name }}
        </option>
      </select>
      <select
        class="h-8 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-xs text-n-slate-12"
        :value="modelValue.inboxId"
        @change="update('inboxId', $event.target.value)"
      >
        <option value="">{{ t('INBOX.REMINDER_FILTER.ALL_INBOXES') }}</option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
      <input
        type="date"
        class="h-8 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-xs text-n-slate-12"
        :aria-label="t('INBOX.REMINDER_FILTER.FROM_DATE')"
        :value="modelValue.fromDate"
        @change="update('fromDate', $event.target.value)"
      />
      <input
        type="date"
        class="h-8 rounded-md border border-n-weak bg-n-alpha-1 px-2 text-xs text-n-slate-12"
        :aria-label="t('INBOX.REMINDER_FILTER.TO_DATE')"
        :value="modelValue.toDate"
        @change="update('toDate', $event.target.value)"
      />
      <button
        type="button"
        class="col-span-2 text-right text-xs text-n-blue-11"
        @click="reset"
      >
        {{ t('INBOX.REMINDER_FILTER.RESET') }}
      </button>
    </div>
  </div>
</template>
